import { supabase } from '@/lib/supabase';
import { Attachment } from '@/types/transaction';
import { validateAndProcessImage } from './imageProcessing';
import { v4 as uuidv4 } from 'uuid';

const dataURLtoBlob = (dataURL: string): Blob => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || '';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

const blobToFile = (blob: Blob, fileName: string): File => {
  return new File([blob], fileName, { type: blob.type });
};

export const uploadAttachment = async (
  _userId: string,
  transactionId: string,
  file: File
): Promise<Attachment> => {
  try {
    // Check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('Authentication error: User not authenticated');
      throw new Error('User not authenticated');
    }

    // Use the current user's ID instead of the provided one for security
    const secureUserId = user.id;
    
    const fileId = uuidv4();
    const fileExtension = file.name.split('.').pop() || '';
    const normalizedExtension = fileExtension ? `.${fileExtension}` : '';
    const fileName = `${fileId}${normalizedExtension}`;
    const filePath = `users/${secureUserId}/transactions/${transactionId}/attachments/${fileName}`;

    console.log('Uploading file:', {
      filePath,
      fileName,
      extension: normalizedExtension,
      type: file.type
    });

    let fileToUpload: File = file;

    // Process image if it's an image file
    if (file.type.startsWith('image/')) {
      try {
        const processedImage = await validateAndProcessImage(file);
        if (processedImage.isValid && processedImage.processedImage) {
          const blob = dataURLtoBlob(processedImage.processedImage);
          fileToUpload = blobToFile(blob, file.name);
        }
      } catch (error) {
        console.error('Error processing image:', error);
        // Continue with original file if image processing fails
      }
    }

    // Create metadata
    const metadata = {
      userId: secureUserId,
      transactionId,
      originalName: fileToUpload.name,
      uploadedAt: new Date().toISOString()
    };

    // Upload file to Supabase Storage
    const { data, error } = await supabase.storage
      .from('attachments')
      .upload(filePath, fileToUpload, {
        contentType: fileToUpload.type,
        upsert: true,
        metadata
      });

    if (error) {
      console.error('Error uploading to Supabase Storage:', error);
      throw error;
    }

    if (!data) {
      throw new Error('Upload failed: No data returned');
    }

    // Get the public URL
    const { data: urlData } = supabase.storage
      .from('attachments')
      .getPublicUrl(filePath);

    if (!urlData || !urlData.publicUrl) {
      throw new Error('Failed to get public URL');
    }

    const downloadURL = urlData.publicUrl;
    const now = new Date();

    const attachment: Attachment = {
      id: fileId,
      fileName: file.name,
      fileUrl: downloadURL,
      fileType: fileToUpload.type,
      uploadedAt: now,
      size: fileToUpload.size,
      name: file.name,
      type: fileToUpload.type,
      url: downloadURL,
      userId: secureUserId,
      createdAt: now,
      updatedAt: now
    };

    console.log('Created attachment:', attachment);
    return attachment;

  } catch (error) {
    console.error('Error uploading attachment:', error);
    throw error;
  }
};

export const uploadMultipleAttachments = async (
  userId: string,
  transactionId: string,
  files: File[]
): Promise<Attachment[]> => {
  try {
    const uploadPromises = files.map(file => 
      uploadAttachment(userId, transactionId, file)
    );
    
    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error('Error uploading multiple files:', error);
    throw new Error('Failed to upload one or more files. Please try again.');
  }
};

export const deleteTransactionAttachment = async (
  userId: string,
  transactionId: string,
  attachment: Attachment
): Promise<void> => {
  try {
    console.log('Deleting attachment:', { userId, transactionId, attachment });

    if (!attachment) {
      throw new Error('No attachment provided');
    }

    // Check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('Authentication error: User not authenticated');
      throw new Error('User not authenticated');
    }

    // Use the current user's ID instead of the provided one for security
    const secureUserId = user.id;

    // Extract file path from URL or construct it
    let filePath = '';

    // Try to extract path from the Supabase URL
    if (attachment.fileUrl) {
      try {
        // Extract the path from the URL
        const urlParts = attachment.fileUrl.split('/attachments/')[1];
        if (urlParts) {
          filePath = urlParts;
          console.log('Using path from fileUrl:', filePath);
        } else {
          throw new Error('Invalid fileUrl format');
        }
      } catch (error) {
        console.log('Failed to extract path from fileUrl:', error);
        filePath = '';
      }
    }

    // If we couldn't get the path from fileUrl, construct it
    if (!filePath) {
      // Get the extension from fileName if available
      const extension = attachment.fileName 
        ? `.${attachment.fileName.split('.').pop()}`
        : '';

      filePath = `users/${secureUserId}/transactions/${transactionId}/attachments/${attachment.id}${extension}`;
      console.log('Using constructed path:', filePath);
    }

    // Delete the file from Supabase Storage
    const { error } = await supabase.storage
      .from('attachments')
      .remove([filePath]);

    if (error) {
      console.error('Error deleting file from Supabase Storage:', error);
      // If the file doesn't exist, we can proceed
      if (error.message.includes('Not Found')) {
        console.log('File not found in storage, proceeding with database update');
        return;
      }
      throw error;
    }

    console.log('Successfully deleted file at path:', filePath);

  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

// Base64 conversion utility
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
  });
};
